// src/components/ThemeToggle.jsx
//
// Animated light/dark switch. Light = sun with rotating rays and drifting
// clouds; dark = crescent moon over a twinkling starfield. Pure CSS/SVG, no deps.
// Reads/writes the app theme via ThemeContext. Drop it anywhere; here it lives in
// the sidebar (AdminLayout/Navigation).

import React from 'react';
import { useTheme } from '../context/ThemeContext';

const STYLES = `
  .nd-toggle {
    --ndt-day-1: #8ec9ff; --ndt-day-2: #cfe9ff;
    --ndt-night-1: #1b2540; --ndt-night-2: #0c1024;
    appearance: none; border: 0; padding: 0; margin: 0; cursor: pointer;
    background: transparent; line-height: 0; border-radius: 999px;
    outline: none;
  }
  .nd-toggle:focus-visible .ndt-track { box-shadow: 0 0 0 3px rgba(96,165,250,0.55); }

  .ndt-track {
    position: relative; display: inline-block;
    width: 54px; height: 28px; border-radius: 999px;
    overflow: hidden; isolation: isolate;
    border: 1px solid rgba(255,255,255,0.18);
    box-shadow: inset 0 1px 3px rgba(0,0,0,0.35);
    transition: border-color 0.4s ease, box-shadow 0.4s ease;
  }

  /* ── Sky layers (cross-fade) ─────────────────────────────────── */
  .ndt-sky { position: absolute; inset: 0; transition: opacity 0.5s ease; }
  .ndt-sky-day   { background: linear-gradient(160deg, var(--ndt-day-1), var(--ndt-day-2)); opacity: 1; }
  .ndt-sky-night { background: linear-gradient(160deg, var(--ndt-night-1), var(--ndt-night-2)); opacity: 0; }
  .nd-toggle[data-mode='dark'] .ndt-sky-day   { opacity: 0; }
  .nd-toggle[data-mode='dark'] .ndt-sky-night { opacity: 1; }

  /* ── Stars (only at night) ───────────────────────────────────── */
  .ndt-stars { position: absolute; inset: 0; opacity: 0; transition: opacity 0.5s ease 0.05s; z-index: 1; }
  .nd-toggle[data-mode='dark'] .ndt-stars { opacity: 1; }
  .ndt-star { position: absolute; background: #fff; border-radius: 50%;
    box-shadow: 0 0 3px rgba(255,255,255,0.8); animation: ndt-twinkle 2.4s infinite ease-in-out; }
  .ndt-star.s1 { width: 2px; height: 2px; top: 6px;  left: 8px;  animation-delay: 0s;   }
  .ndt-star.s2 { width: 2px; height: 2px; top: 15px; left: 14px; animation-delay: 0.5s; }
  .ndt-star.s3 { width: 1.5px;height: 1.5px;top: 19px;left: 6px;  animation-delay: 1s;   }
  .ndt-star.s4 { width: 2.5px;height: 2.5px;top: 4px;  left: 20px; animation-delay: 1.4s; }
  .ndt-star.s5 { width: 1.5px;height: 1.5px;top: 11px; left: 24px; animation-delay: 0.8s; }
  @keyframes ndt-twinkle { 0%,100% { opacity: 0.35; transform: scale(0.8); } 50% { opacity: 1; transform: scale(1.15); } }

  /* ── Clouds (only by day) ────────────────────────────────────── */
  .ndt-clouds { position: absolute; inset: 0; opacity: 1; transition: opacity 0.45s ease; z-index: 2; pointer-events: none; }
  .nd-toggle[data-mode='dark'] .ndt-clouds { opacity: 0; }
  .ndt-cloud { position: absolute; background: #fff; border-radius: 999px;
    box-shadow: 0 0 0 rgba(0,0,0,0); animation: ndt-drift 6s infinite ease-in-out; }
  .ndt-cloud::before, .ndt-cloud::after { content: ''; position: absolute; background: #fff; border-radius: 50%; }
  .ndt-cloud.c1 { width: 14px; height: 6px; right: 6px;  bottom: 5px; opacity: 0.95; }
  .ndt-cloud.c1::before { width: 7px; height: 7px; left: 2px;  top: -3px; }
  .ndt-cloud.c1::after  { width: 6px; height: 6px; right: 2px; top: -2px; }
  .ndt-cloud.c2 { width: 9px;  height: 4px; right: 20px; top: 6px; opacity: 0.7; animation-delay: 1.2s; }
  .ndt-cloud.c2::before { width: 5px; height: 5px; left: 1px; top: -2px; }
  @keyframes ndt-drift { 0%,100% { transform: translateX(0); } 50% { transform: translateX(-3px); } }

  /* ── Sliding knob (sun ↔ moon) ───────────────────────────────── */
  .ndt-knob { position: absolute; top: 3px; left: 3px; width: 22px; height: 22px;
    border-radius: 50%; z-index: 3;
    transition: transform 0.5s cubic-bezier(0.34,1.4,0.5,1); }
  .nd-toggle[data-mode='dark'] .ndt-knob { transform: translateX(26px); }

  .ndt-sun, .ndt-moon { position: absolute; inset: 0; border-radius: 50%;
    transition: opacity 0.4s ease, transform 0.5s cubic-bezier(0.34,1.4,0.5,1); }

  /* Sun */
  .ndt-sun { background: radial-gradient(circle at 35% 35%, #fff3c4, #ffcf3f 60%, #f9b219);
    box-shadow: 0 0 8px rgba(255,193,40,0.9), 0 0 2px rgba(255,193,40,0.9); opacity: 1; transform: scale(1); }
  .ndt-sun::after { content: ''; position: absolute; inset: -5px; border-radius: 50%;
    background: repeating-conic-gradient(#ffce46 0deg 5deg, transparent 5deg 45deg);
    -webkit-mask: radial-gradient(circle, transparent 56%, #000 58%);
            mask: radial-gradient(circle, transparent 56%, #000 58%);
    animation: ndt-spin 9s linear infinite; opacity: 0.85; }
  .nd-toggle[data-mode='dark'] .ndt-sun { opacity: 0; transform: scale(0.3) rotate(-40deg); }
  @keyframes ndt-spin { to { transform: rotate(360deg); } }

  /* Moon (crescent carved by an offset disc tinted like the night sky) */
  .ndt-moon { background: radial-gradient(circle at 38% 35%, #fdfdff, #d7deec 70%);
    box-shadow: inset -1px -1px 2px rgba(120,130,160,0.5), 0 0 6px rgba(220,230,255,0.4);
    opacity: 0; transform: scale(0.3) rotate(40deg); }
  .ndt-moon::before { content: ''; position: absolute; top: -3px; right: -2px;
    width: 18px; height: 18px; border-radius: 50%;
    background: var(--ndt-night-2); }
  .ndt-moon::after { content: ''; position: absolute; border-radius: 50%;
    background: rgba(150,160,185,0.55);
    width: 4px; height: 4px; left: 5px; bottom: 5px;
    box-shadow: 4px 3px 0 -1px rgba(150,160,185,0.5), -1px -4px 0 -1.5px rgba(150,160,185,0.45); }
  .nd-toggle[data-mode='dark'] .ndt-moon { opacity: 1; transform: scale(1) rotate(0deg); }

  @media (prefers-reduced-motion: reduce) {
    .nd-toggle *, .ndt-knob, .ndt-sun, .ndt-moon, .ndt-sky { transition: none !important; animation: none !important; }
  }
`;

const ThemeToggle = ({ className = '' }) => {
  const { theme, toggleTheme } = useTheme();
  const isDark = theme === 'dark';

  return (
    <button
      type="button"
      className={`nd-toggle ${className}`}
      data-mode={theme}
      onClick={toggleTheme}
      role="switch"
      aria-checked={isDark}
      aria-label={`Switch to ${isDark ? 'light' : 'dark'} mode`}
      title={`Switch to ${isDark ? 'light' : 'dark'} mode`}
    >
      <style>{STYLES}</style>
      <span className="ndt-track">
        <span className="ndt-sky ndt-sky-day" />
        <span className="ndt-sky ndt-sky-night" />
        <span className="ndt-stars">
          <span className="ndt-star s1" />
          <span className="ndt-star s2" />
          <span className="ndt-star s3" />
          <span className="ndt-star s4" />
          <span className="ndt-star s5" />
        </span>
        <span className="ndt-clouds">
          <span className="ndt-cloud c1" />
          <span className="ndt-cloud c2" />
        </span>
        <span className="ndt-knob">
          <span className="ndt-sun" />
          <span className="ndt-moon" />
        </span>
      </span>
    </button>
  );
};

export default ThemeToggle;
